<template>
  <div class="hook-row group flex flex-row items-center  p-2">
    <div ref="thumbnailContainer" class="thumbnail-container"
    @mouseenter="handleThumbnailHover(true)" @mouseleave="handleThumbnailHover(false)">
      <!-- Thumbnail block: single (1 ad) or stacked-with-count-badge (multi-ad) -->
      <div v-if="isMultiAd" class="stack-wrap relative flex-shrink-0">
        <!-- Three stacked 36x36 thumbnails at 9px offsets (offsets in scoped CSS) -->
        <div v-for="(thumb, idx) in stackedThumbnails" :key="`stack-${idx}`"
        class="stack-tile absolute w-9 h-9 rounded-md overflow-hidden bg-border-pressed"
        :class="{ 'opacity-50': !thumb }">
          <img v-if="thumb" :src="thumb" alt="" class="w-full h-full object-cover">
        </div>
        <!-- Count badge (chip overlapping right edge of stack) -->
        <div class="count-badge absolute flex items-center justify-center bg-white">
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ group.totalAds }}
          </BaseText>
        </div>
      </div>
      <!-- Single thumbnail (pinned rows, 1-ad hooks) -->
      <div v-else class="relative flex items-center justify-center h-9 rounded-md bg-border-pressed border border-white overflow-hidden z-10 flex-shrink-0"
      :class="{ 'opacity-50': !thumbnail }" style="width: 67px;">
        <img v-if="thumbnail" :src="thumbnail" alt="" class="w-full h-full object-cover">
      </div>
    </div>

    <!-- Pin / Unpin toggle -->
    <button class="ml-2 flex items-start overflow-clip p-1.5 rounded-md flex-shrink-0 transition-colors"
    :class="isPinned ? 'pin-toggle-active' : 'hover:bg-background-hover'"
    :aria-label="isPinned ? 'Unpin hook' : 'Pin hook'"
    @click="onPinToggle">
      <PinOutlineIcon v-if="!isPinned" class="text-icon-normal" />
      <UnpinIcon v-else class="pin-toggle-active-icon" />
    </button>

    <!-- Copy hook text button -->
    <button class="ml-1 p-1.5 rounded-md flex-shrink-0 transition-colors hover:bg-background-hover"
    @click="copyHookText">
      <CopyIcon class="text-icon-normal" />
    </button>

    <!-- Hook text pill (flex-grow) -->
    <div class="hook-text-container ml-2 flex flex-grow min-w-0 items-center">
      <div class="flex flex-grow min-w-0 items-center p-0.5 rounded-lg bg-background-normal h-9 px-3">
        <BaseText type="body" size="sm" class="text-text-muted flex-shrink-0">"</BaseText>
        <BaseText type="body" size="sm" class="hook-text flex-shrink min-w-0 text-text-muted truncate" v-html="highlightedHook" />
        <BaseText type="body" size="sm" class="text-text-muted flex-shrink-0">"</BaseText>
      </div>
    </div>

    <!-- Days running pill (green dot + duration) -->
    <div class="ml-2 flex items-center gap-1.5 px-2 flex-shrink-0">
      <div class="flex items-center justify-center w-5 h-5">
        <span class="rounded-full w-2 h-2" :class="isActive ? 'dot-active' : 'bg-border-pressed'" />
      </div>
      <BaseText type="body" size="sm" class="text-text-muted whitespace-nowrap">
        {{ daysRunning }}
      </BaseText>
    </div>

    <!-- See Ads button -->
    <div class="ml-2 flex-shrink-0" style="width: 110px;">
      <button class="see-ads-btn flex items-center gap-1.5 px-2 py-1.5 rounded-md text-text-muted ml-auto"
      @click="handleDetailsClick" @mouseenter="detailsButtonHovered = true" @mouseleave="detailsButtonHovered = false">
        <div class="w-5 h-5">
          <AdDetailsAnimatedIcon :isHovered="detailsButtonHovered" />
        </div>
        <BaseText type="label" size="sm" class="text-text-muted whitespace-nowrap">
          {{ isMultiAd ? 'See Ads' : 'Ad Details' }}
        </BaseText>
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { getAdEffectiveEndDate } from '@/utils/dateUtils'
import PinOutlineIcon from '../../globals/Icons/SpyderV2Icons/PinOutlineIcon.vue'
import UnpinIcon from '../../globals/Icons/SpyderV2Icons/UnpinIcon.vue'
import AdDetailsAnimatedIcon from '../../globals/Icons/AdDetailsAnimatedIcon.vue'
import CopyIcon from '../../globals/Icons/CopyIconStatic.vue'

export default {
  name: 'HookRowV2',
  components: {
    PinOutlineIcon,
    UnpinIcon,
    AdDetailsAnimatedIcon,
    CopyIcon
  },
  props: {
    group: {
      type: Object,
      required: true
    },
    isPinned: {
      type: Boolean,
      default: false
    },
    isSearch: {
      type: Boolean,
      default: false
    },
    textSearch: {
      type: String,
      default: ''
    },
    sort: {
      type: String,
      default: 'longest_running'
    }
  },
  data () {
    return {
      detailsButtonHovered: false,
      hookTextHovered: false
    }
  },
  computed: {
    highlightedHook () {
      const text = this.group?.displayHook || ''
      const query = (this.textSearch || '').trim()
      if (!this.isSearch || !query) {
        return this.escapeHtml(text)
      }

      let html = ''
      const lowerText = text.toLowerCase()
      const lowerQuery = query.toLowerCase()
      let cursor = 0
      let matchIndex = lowerText.indexOf(lowerQuery, cursor)

      while (matchIndex !== -1) {
        if (matchIndex > cursor) {
          html += this.escapeHtml(text.slice(cursor, matchIndex))
        }
        html += `<span class="hook-highlight">${this.escapeHtml(text.slice(matchIndex, matchIndex + query.length))}</span>`
        cursor = matchIndex + query.length
        matchIndex = lowerText.indexOf(lowerQuery, cursor)
      }

      if (cursor < text.length) {
        html += this.escapeHtml(text.slice(cursor))
      }

      return html
    },
    representativeAd () {
      // longest-running ad (the hook's proven/long-lived creative).
      return this.group?.longestRunningAd || null
    },
    isMultiAd () {
      return (this.group?.totalAds || 0) > 1
    },
    stackedThumbnails () {
      // If a future API exposes preview siblings, swap this for the real array.
      return this.group?.totalAds < 3
      ? [this.thumbnail, this.thumbnail]
      : [this.thumbnail, this.thumbnail, this.thumbnail]
    },
    thumbnail () {
      const ad = this.representativeAd
      if (!ad) return ''
      if (ad.thumbnail) return ad.thumbnail
      const firstCard = Array.isArray(ad.cards) ? ad.cards[0] : null
      return firstCard?.thumbnail || firstCard?.image || ad.avatar || ''
    },
    isActive () {
      return (this.group?.activeAds || 0) > 0
    },
    daysRunning () {
      const ad = this.representativeAd
      if (!ad?.startedRunning) return 'N/A'

      let endTime = getAdEffectiveEndDate(ad)
      if (isNaN(endTime)) endTime = new Date().getTime()

      const startDate = moment.utc(ad.startedRunning)
      const endDate = moment.utc(endTime)
      const duration = Math.ceil(moment.duration(endDate.diff(startDate)).as('days'))

      if (duration < 0) return 'N/A'
      const displayDays = duration === 0 ? '<1' : duration
      const dayLabel = duration > 1 ? 'Days' : 'Day'
      return `${displayDays} ${dayLabel}`
    }
  },
  methods: {
    onPinToggle () {
      if (this.isPinned) {
        this.$emit('unpin')
      } else {
        this.$emit('pin')
      }
    },
    handleDetailsClick () {
      if (this.isMultiAd) {
        this.$emit('see-ads', this.group)
      } else {
        this.$emit('open-ad', this.representativeAd)
      }
    },
    handleThumbnailHover (entering) {
      if (!entering) {
        this.$emit('hover-thumbnail', { elm: null, media: null })
        return
      }

      const thumbnailContainer = this.$refs.thumbnailContainer
      const videoSrc = this.representativeAd?.video || this.representativeAd?.cards?.[0]?.video || null
      if (!thumbnailContainer || !videoSrc) return

      this.$emit('hover-thumbnail', { elm: thumbnailContainer, media: videoSrc })
    },
    escapeHtml (str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    copyHookText () {
      navigator.clipboard.writeText(this.group.displayHook)
      this.$showAlert({
        type: 'success',
        message: 'Copied',
        timeout: 1750
      })
    }
  }
}
</script>

<style scoped>
/* Off-scale pixel values live here (Tailwind 2 / no-JIT has no arbitrary values). */

/* Row container radius (10px — not on the default scale) */
.hook-row {
  border-radius: 10px;
}

/* Multi-ad stacked-thumbnail block: 3 tiles (36px) at 9px offsets + count badge */
.stack-wrap {
  width: 67px;
  height: 36px;
}
.stack-tile {
  top: 0;
  border: 1px solid white;
}
.stack-tile:nth-child(1) { left: 0; z-index: 1; }
.stack-tile:nth-child(2) { left: 9px; z-index: 2; }
.stack-tile:nth-child(3) { left: 18px; z-index: 3; }

/* See Ads button — matches Figma "Button/Secondary/Normal" token */
.see-ads-btn {
  background: linear-gradient(180deg, rgba(223, 225, 231, 0.08) 0%, rgba(223, 225, 231, 0.08) 100%), #FFF;
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out;
}
.see-ads-btn:hover {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.21), 0 0 0 1px rgba(0, 56, 108, 0.16);
}

/* Stack count badge — chip overlapping the right edge of the stack */
.count-badge {
  left: 39px;
  top: 4px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  z-index: 4;
  box-shadow:
    0px 1px 2px 0px rgba(9, 25, 72, 0.13),
    0px 0px 0px 1px rgba(18, 55, 105, 0.08);
}

/* Active pin toggle (pinned state) — Figma Primary/Spyder/Solid 0 + 200 */
.pin-toggle-active {
  background-color: #FDF2F1;
  transition: background-color 150ms ease-in-out;
}
.pin-toggle-active:hover {
  background-color: #F9CFCD;
}
.pin-toggle-active-icon {
  color: #AB2D27;
}

.thumbnail-container {
  position: relative;
  isolation: isolate;
}
.thumbnail-container::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 10px;
  background-color: transparent;
  transition: background-color 150ms ease-in-out;
}
.thumbnail-container:hover::after {
  background-color: #ECEFF3;
}

/* Active "days running" status dot — Figma success green */
.dot-active {
  background-color: #00A879;
}

.hook-text {
  position: relative;
  z-index: 1;
}
/* Search-match highlight inside the hook text pill */
.hook-text-container ::v-deep .hook-highlight {
  position: relative;
  height: 20px;
  color: #AB2D27;
}
.hook-text-container ::v-deep .hook-highlight::after {
  content: '';
  position: absolute;
  left: -1px;
  right: -1px;
  top: 1px;
  bottom: -1px;
  background-color: #F9CFCD;
  border-radius: 4px;
  z-index: -1;
}

.v-enter-active, .v-leave-active {
  transition: opacity 50ms ease-in-out;
}
.v-enter, .v-enter-from, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>
